import { useCallback, useEffect, useState } from 'react';
import { AutoComplete, Input, Space, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { DefaultOptionType } from 'antd/es/select';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  mapboxApiClient,
  type Suggestion,
} from '../../infrastructure/mapbox/client';
import { useDebounce } from '../../hooks/useDebounce';
import { useSearchedLocation } from '../../hooks/SimViewer/useSearchedLocation';
import { useMapViewStateUpdater } from '../../hooks/SimViewer/useMapViewState';

type SuggestionOption = DefaultOptionType & {
  data: Suggestion;
};

const SearchLocation = () => {
  const { setSearchedLocation, mapboxSessionToken, setMapboxSessionToken } =
    useSearchedLocation();
  const { updateMapViewState } = useMapViewStateUpdater();
  const { debounce } = useDebounce(1000);
  const { t } = useTranslation();

  const [selectedValue, setSelectedValue] = useState<string>('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const refreshMapboxSessionToken = useCallback(() => {
    const newSessionToken = {
      token: uuidv4(),
      expiredAt: Date.now() + 60 * 60 * 1000,
    };
    setMapboxSessionToken(newSessionToken);
  }, [setMapboxSessionToken]);

  const truncateTexts = useCallback(
    (featureName: string, description: string, maxLength = 25) => {
      const combinedText = featureName + description;

      if (combinedText.length <= maxLength) {
        return {
          truncatedFeatureName: featureName,
          truncatedDescription: description,
        };
      }

      const remainingLength = maxLength - featureName.length;
      if (remainingLength > 0) {
        return {
          truncatedFeatureName: featureName,
          truncatedDescription: description.substring(0, remainingLength) + '…',
        };
      } else {
        return {
          truncatedFeatureName: featureName.substring(0, maxLength) + '…',
          truncatedDescription: '',
        };
      }
    },
    []
  );

  const handleSearch = (searchValue: string): void => {
    if (searchValue.length > 0) {
      debounce(async (): Promise<void> => {
        if (!mapboxSessionToken || mapboxSessionToken.expiredAt < Date.now()) {
          refreshMapboxSessionToken();
          return;
        }

        const suggestions = await mapboxApiClient.searchMapPlaces(
          searchValue,
          mapboxSessionToken?.token
        );

        const searchResult = suggestions.filter((suggestion: Suggestion) => {
          // poiの場合は、カテゴリの値があるもののみ表示する
          return (
            !suggestion.result_type.includes('poi') ||
            suggestion.category?.length
          );
        });

        setSuggestions(searchResult);
      });
    } else {
      setSuggestions([]);
      setSearchedLocation(undefined);
    }
  };

  const handleSelect = async (
    value: string,
    option: SuggestionOption
  ): Promise<void> => {
    const { data } = option;

    if (data) {
      if (!mapboxSessionToken || mapboxSessionToken.expiredAt < Date.now()) {
        refreshMapboxSessionToken();
        return;
      }

      setSelectedValue(data.feature_name);
      const point = await mapboxApiClient.retrieveSearchResult(
        value,
        mapboxSessionToken?.token
      );

      if (point) {
        // 地図を選択した地点に移動する(地図の傾きは保持したままにする)
        updateMapViewState({
          longitude: point[0],
          latitude: point[1],
          zoom: 15,
          bearing: 0,
        });

        // 選択した地点にピンを立てる
        setSearchedLocation({ longitude: point[0], latitude: point[1] });
      }
    }
  };

  useEffect(() => {
    if (!mapboxSessionToken || mapboxSessionToken.expiredAt < Date.now()) {
      refreshMapboxSessionToken();
    }
  }, [mapboxSessionToken, refreshMapboxSessionToken, setMapboxSessionToken]);

  const options = suggestions.map(
    (suggestion: Suggestion): SuggestionOption => {
      const { truncatedFeatureName, truncatedDescription } = truncateTexts(
        suggestion.feature_name,
        suggestion.description
      );

      return {
        value: suggestion.action.body.id,
        label: (
          <Space
            key={suggestion.action.body.id}
            size={12}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography.Text strong>{truncatedFeatureName}</Typography.Text>
            <Typography.Text type="secondary">
              {truncatedDescription}
            </Typography.Text>
          </Space>
        ),
        data: suggestion,
      };
    }
  );

  return (
    <AutoComplete
      value={selectedValue}
      options={options}
      onSelect={handleSelect}
      onSearch={handleSearch}
      onChange={(value) => setSelectedValue(value)}
    >
      <Input
        size="large"
        style={{ width: 380 }}
        placeholder={t('searchOnMap')}
        suffix={
          <CloseOutlined
            onClick={() => {
              setSelectedValue('');
              setSuggestions([]);
              setSearchedLocation(undefined);
            }}
          />
        }
      />
    </AutoComplete>
  );
};

export { SearchLocation };
